import type { CountryCode, Currency, SupportedLanguage } from './countries'
import type { PaymentResponse } from '@adyen/api-library/lib/src/typings/checkout/paymentResponse'
import type { CreateCheckoutSessionResponse } from '@adyen/api-library/lib/src/typings/checkout/createCheckoutSessionResponse'
import type { DetailsRequest } from '@adyen/api-library/lib/src/typings/checkout/detailsRequest'
import type { PaymentRequest } from '@adyen/api-library/lib/src/typings/checkout/paymentRequest'
import type { PaymentAction } from '@adyen/adyen-web/dist/types/types'
import type { EligibilityStatus } from './scid'
import type { ProductV2 } from '..'
import { z } from 'zod'

type PurchaseProduct = {
  id: string
  quantity: number
}

export interface PurchaseInitializeBody {
  countryCode: string
  shopperLocale: string
  currency: Currency
  product: {
    id: string
    quantity: number
  }
}

export interface SubmitPurchaseBody {
  countryCode: string
  shopperLocale: string
  product: PurchaseProduct
  currency: Currency
  lang: SupportedLanguage
  paymentMethod: {
    type: string
  }
  storePaymentMethod: boolean
  browserInfo: Record<string, unknown>
  billingAddress: Record<string, unknown>
  creatorBoostCode: string
}

export interface PurchaseCheckoutResponse {
  purchaseId: string
  resultCode: PaymentResponse['resultCode'] | null
  action: PaymentAction | null
  outputDetails: {
    [name: string]: string
  } | null
}

export type PurchasesCheckoutValidateResponse = EligibilityStatus

export type PurchasesCheckoutValidateRequest = {
  product: ProductV2
  quantity: number
}[]

export type PurchaseDetailsBody = {
  purchaseId: string
  details: Record<string, unknown>
  paymentData: string
}

export interface PurchaseTaxPercentageResponse {
  tax: number
}

export type PurchasesCheckoutSessionRequest = {
  countryCode: CountryCode
  language: SupportedLanguage
  product: {
    id: string
    quantity: number
  }
  creatorBoostCode: string | undefined
}

type PurchasesCheckoutSessionBaseResponse = { paymentProvider: PaymentProvider }
export type PurchasesCheckoutXsollaSessionResponse = PurchasesCheckoutSessionBaseResponse & { token: string }
export type PurchasesCheckoutAdyenSessionResponse = PurchasesCheckoutSessionBaseResponse &
  CreateCheckoutSessionResponse & {
    countryCode: CountryCode
  }
export type PurchasesCheckoutSessionResponse =
  | PurchasesCheckoutXsollaSessionResponse
  | PurchasesCheckoutAdyenSessionResponse

export const isXsollaSessionResponse = (
  session?: PurchasesCheckoutSessionResponse,
): session is PurchasesCheckoutXsollaSessionResponse => {
  return session !== undefined && session.paymentProvider === 'XSOLLA'
}

export const isAdyenSessionResponse = (
  session?: PurchasesCheckoutSessionResponse,
): session is PurchasesCheckoutAdyenSessionResponse => {
  return session !== undefined && session.paymentProvider === 'ADYEN'
}

export type PurchasesCheckoutProcessRequest = {
  purchaseId: string
  paymentMethodName: string
  data: { paymentMethod: PaymentRequest['paymentMethod']; additionalData: PaymentRequest['additionalData'] } & Record<
    string,
    unknown
  >
}

export type PurchasesCheckoutProcessResponse = PaymentResponse

export type PurchaseDetailsRequest = Pick<DetailsRequest, 'details' | 'paymentData'> & { purchaseId: string }
export type PurchaseDetailsResponse = {
  purchaseId: string
  resultCode: PaymentResponse['resultCode']
  action: PaymentAction | null
  outputDetails: {
    [name: string]: string
  } | null
}

export const AdyenPaymentProvider = z.literal('ADYEN')
export const XsollaPaymentProvider = z.literal('XSOLLA')
export const RazerPaymentProvider = z.literal('RAZER_GOLD')
export const PaymentProvider = z.union([AdyenPaymentProvider, XsollaPaymentProvider, RazerPaymentProvider])
export const LegacyPaymentProvider = z.union([AdyenPaymentProvider, XsollaPaymentProvider])

export type PaymentProviderAdyen = z.infer<typeof AdyenPaymentProvider>
export type PaymentProviderXsolla = z.infer<typeof XsollaPaymentProvider>
export type PaymentProviderRazer = z.infer<typeof RazerPaymentProvider>
export type PaymentProvider = z.infer<typeof PaymentProvider>
export type LegacyPaymentProvider = z.infer<typeof LegacyPaymentProvider>

export type SalesTax = 'SALES_TAX'
export type VAT = 'VAT'
export type TaxType = SalesTax | VAT

export const AdyenCheckoutSessionInputSchema = z.object({
  id: z.string(),
  revision: z.number(),
})

export enum RazerPaymentChannel {
  RAZER_GOLD_WALLET = 1,
  RAZER_GOLD_PIN = 3,
}

export const razerPaymentChannelEnumToName = (paymentChannel: RazerPaymentChannel): string => {
  switch (paymentChannel) {
    case RazerPaymentChannel.RAZER_GOLD_WALLET:
      return 'Razer Gold Wallet'
    case RazerPaymentChannel.RAZER_GOLD_PIN:
      return 'Razer Gold PIN'
    default:
      throw 'Invalid Razer Gold payment channel'
  }
}

export const RazerPaymentChannelIdentifier = z.union([z.literal('RazerGoldWallet'), z.literal('RazerGoldPIN')])
export type RazerPaymentChannelIdentifier = z.infer<typeof RazerPaymentChannelIdentifier>

export const razerPaymentChannelToIdentifier = (paymentChannel: RazerPaymentChannel): RazerPaymentChannelIdentifier => {
  switch (paymentChannel) {
    case RazerPaymentChannel.RAZER_GOLD_WALLET:
      return 'RazerGoldWallet'
    case RazerPaymentChannel.RAZER_GOLD_PIN:
      return 'RazerGoldPIN'
    default:
      throw 'Invalid Razer Gold payment channel'
  }
}
